import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {MilestoneCreate} from '@github-ui/repository-milestone/MilestoneCreate'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {useQueryContext} from '../../contexts/QueryContext'
import {useEntryPointsLoader} from '../../hooks/use-entrypoint-loaders'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import type {RepositoryMilestoneNewPageContentInternal$key} from './__generated__/RepositoryMilestoneNewPageContentInternal.graphql'
import type {RepositoryMilestoneNewPageQuery} from './__generated__/RepositoryMilestoneNewPageQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestoneNewPageQuery($name: String!, $owner: String!) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryMilestoneNewPageContentInternal
    }
  }
`

export const RepositoryMilestoneNewPage: EntryPointComponent<
  {pageQuery: RepositoryMilestoneNewPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone New">
      <RepositoryMilestoneNewPageContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneNewPageContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestoneNewPageQuery>
}) {
  const pageData = usePreloadedQuery<RepositoryMilestoneNewPageQuery>(PageQuery, pageQueryRef)

  const {setCurrentViewId} = useQueryContext()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestoneNewPageContentInternal repository={okResult.value} />
}

function RepositoryMilestoneNewPageContentInternal({
  repository,
}: {
  repository: RepositoryMilestoneNewPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestoneNewPageContentInternal on Repository {
        ...MilestoneCreateFormRepositoryQuery
      }
    `,
    repository,
  )

  return <MilestoneCreate repository={data} />
}
